import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Breadcrumb } from './Breadcrumb';
import type { AdminRole } from '@/types/admin';

const ROLE_BADGE: Record<AdminRole, { label: string; className: string }> = {
  super_admin: { label: 'Super Admin', className: 'bg-purple-100 text-purple-700' },
  admin: { label: 'Admin', className: 'bg-blue-100 text-blue-700' },
  moderator: { label: 'Moderator', className: 'bg-yellow-100 text-yellow-700' },
  analyst: { label: 'Analyst', className: 'bg-green-100 text-green-700' },
  support: { label: 'Support', className: 'bg-gray-100 text-gray-600' },
};

export function Header() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const badge = session ? ROLE_BADGE[session.role] : null;

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-4 flex-shrink-0">
      {/* Breadcrumb — fills available space */}
      <div className="flex-1 min-w-0">
        <Breadcrumb />
      </div>

      {/* Right side: impersonation indicator, user info, logout */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Impersonation banner (inline in header when active) */}
        {session?.isImpersonating && (
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Impersonating
          </span>
        )}

        {session && (
          <div className="flex items-center gap-2">
            {/* Avatar */}
            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-semibold">
                {session.name.charAt(0).toUpperCase()}
              </span>
            </div>

            {/* Name + role */}
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">{session.name}</p>
              {badge && (
                <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${badge.className}`}>
                  {badge.label}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          title="Sign out"
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
